import { useMemo } from 'react'

import { useQuery } from 'react-query'

import { reconcileSessionQueries } from '@webapp/modules/accounts-payable/query-configs/reconcileSessionQueries'
import { VendorPaymentMethod } from '@webapp/modules/vendor/entities/VendorPaymentMethod'
import { BillApprovalChainOption } from '@webapp/platform/approval-routing'

interface IUseQueryCreditCardBillFormDataParams {
  vendorId: number
  cardCurrencyId: number
  onError?: (err: unknown) => void
}

interface IUseQueryCreditCardBillFormDataReturn {
  paymentMethods: VendorPaymentMethod[]
  approvalChains: BillApprovalChainOption[]
  skipApproval: boolean
  isLoading: boolean
  error: unknown
}

// Sanctioned hydration read (Feature-Set-Composition pattern) — supporting
// data bundled through the `billFormData` use-case query config, not a
// discrete query feature.
export const useQueryCreditCardBillFormData = ({
  vendorId,
  cardCurrencyId,
  onError,
}: IUseQueryCreditCardBillFormDataParams): IUseQueryCreditCardBillFormDataReturn => {
  const { data, isLoading, error } = useQuery({
    ...reconcileSessionQueries.billFormData(vendorId, cardCurrencyId),
    onError,
  })

  const paymentMethods = useMemo(
    () =>
      (data?.paymentMethods ?? []).map(
        (constructor) => new VendorPaymentMethod(constructor)
      ),
    [data]
  )

  const approvalChains = useMemo(
    () =>
      (data?.approvalChains ?? []).map(
        (constructor) => new BillApprovalChainOption(constructor)
      ),
    [data]
  )

  return {
    paymentMethods,
    approvalChains,
    skipApproval: data?.skipApproval ?? false,
    isLoading,
    error,
  }
}
